/**
 * A custom React hook to facilitate sending custom events to Gainsight through the aptrinsic library.
 * For more details, refer to the Gainsight documentation on custom events.
 * @returns {{
 *   trackCustomEvent: (eventName: string, eventData: object) => void
 * }} An object containing a function to send custom events.
 * @example
 * // Usage within a functional component:
 * const { trackCustomEvent } = useGainsightCustomEvent();
 * const handleButtonClick = () => {
 *   const eventData = {
 *     name: 'Welcome Video',
 *     Category: 'Onboarding',
 *     Length: 5000,
 *     'Launched': true,
 *     'Launched date': '2018-03-08T18:11:00Z',
 *   };
 *   trackCustomEvent('Video', eventData);
 * };
 * // Use trackCustomEvent to trigger custom events when needed.
 */
export const useGainsightCustomEvent = () => {
  /**
   * Sends a custom event to Gainsight using the aptrinsic library.
   * @param {string} eventName - The name of the custom event to be tracked in Gainsight.
   * @param {object} eventData - The data associated with the custom event.
   * Refer to Gainsight documentation for the expected format of eventData.
   */
  const trackCustomEvent = (eventName, eventData) => {
    window.aptrinsic && window.aptrinsic("track", eventName, eventData);
  };

  return {
    trackCustomEvent
  };
};
